/**
 * Automation screen (M8) — the trigger-based workflow workspace.
 *
 * Three panels, layered top-down:
 *   1. Rules panel (M8) — a table of every automation rule (name, trigger,
 *      action, delay, enabled toggle, delete) with a "New rule" form (name,
 *      trigger select, action select, action param, delay minutes). Rules map a
 *      guest-lifecycle trigger to an action, optionally after a delay.
 *   2. Fire trigger (M8) — pick a trigger and fire it; the live automation
 *      engine plans + logs the matching rules and shows planned/executed/skipped
 *      counts.
 *   3. Run log (M8) — a table of recent runs (trigger, action, status badge,
 *      detail, time).
 *
 * All backend access goes through the fetch-based `../services.js` layer (web
 * boundary: no `@avos/core-api` import). API error envelopes surface through the
 * shared accessible {@link UserMessage} component.
 *
 * SSR-safe: the initial render is a pure loading state; every network call and
 * any `Date` read runs inside an effect or an event handler, so the server
 * render matches hydration.
 */
import {
  useCallback,
  useEffect,
  useState,
  type FormEvent,
  type ReactElement,
} from "react";
import {
  UserMessage,
  toUserMessage,
  type UserMessageModel,
} from "../../components/UserMessage.js";
import {
  apiCreateAutomationRule,
  apiDeleteAutomationRule,
  apiFireTrigger,
  apiListAutomationRules,
  apiListAutomationRuns,
  apiSetAutomationRuleEnabled,
  asError,
  type AutomationAction,
  type AutomationRule,
  type AutomationRun,
  type AutomationTrigger,
  type FireResult,
} from "../services.js";

/** The lifecycle triggers, with human labels, for the forms + tables. */
const TRIGGERS: readonly { readonly value: AutomationTrigger; readonly label: string }[] =
  [
    { value: "booking_confirmed", label: "Booking confirmed" },
    { value: "check_in", label: "Check-in" },
    { value: "check_out", label: "Check-out" },
    { value: "booking_cancelled", label: "Booking cancelled" },
    { value: "payment_received", label: "Payment received" },
    { value: "review_received", label: "Review received" },
  ];

/** The actions, with human labels, for the form + table. */
const ACTIONS: readonly { readonly value: AutomationAction; readonly label: string }[] =
  [
    { value: "send_message", label: "Send message" },
    { value: "create_task", label: "Create task" },
    { value: "notify_operator", label: "Notify operator" },
    { value: "apply_tag", label: "Apply tag" },
  ];

/** Human label for a trigger value. */
function triggerLabel(trigger: string): string {
  return TRIGGERS.find((t) => t.value === trigger)?.label ?? trigger;
}

/** Human label for an action value. */
function actionLabel(action: string): string {
  return ACTIONS.find((a) => a.value === action)?.label ?? action;
}

/** The placeholder hint for an action's param field. */
function paramHint(action: AutomationAction): string {
  switch (action) {
    case "send_message":
      return "Message template, e.g. “Thanks for staying with us!”";
    case "create_task":
      return "Task title, e.g. “Prepare welcome basket”";
    case "apply_tag":
      return "Tag, e.g. “vip”";
    case "notify_operator":
      return "Note (optional)";
    default:
      return "";
  }
}

// ===========================================================================
// Rules panel (M8)
// ===========================================================================

/** The inline "New rule" creation form. */
function NewRuleForm({
  onCreate,
  busy,
}: {
  readonly onCreate: (input: {
    name: string;
    trigger: AutomationTrigger;
    action: AutomationAction;
    actionParam: string | null;
    delayMinutes: number;
  }) => void;
  readonly busy: boolean;
}): ReactElement {
  const [name, setName] = useState("");
  const [trigger, setTrigger] = useState<AutomationTrigger>("booking_confirmed");
  const [action, setAction] = useState<AutomationAction>("send_message");
  const [actionParam, setActionParam] = useState("");
  const [delay, setDelay] = useState("0");

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    const trimmedName = name.trim();
    if (trimmedName === "") {
      return;
    }
    const trimmedParam = actionParam.trim();
    onCreate({
      name: trimmedName,
      trigger,
      action,
      actionParam: trimmedParam === "" ? null : trimmedParam,
      delayMinutes: Math.max(0, Number(delay || "0")),
    });
    setName("");
    setActionParam("");
    setDelay("0");
  }

  return (
    <form className="console-form" onSubmit={handleSubmit} noValidate>
      <p className="console-form__title">New rule</p>
      <div className="console-form__row">
        <div className="console-field">
          <label htmlFor="auto-new-name">Name</label>
          <input
            id="auto-new-name"
            value={name}
            placeholder="Post-stay thank you"
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="console-field">
          <label htmlFor="auto-new-trigger">Trigger</label>
          <select
            id="auto-new-trigger"
            value={trigger}
            onChange={(e) => setTrigger(e.target.value as AutomationTrigger)}
          >
            {TRIGGERS.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>
        <div className="console-field">
          <label htmlFor="auto-new-action">Action</label>
          <select
            id="auto-new-action"
            value={action}
            onChange={(e) => setAction(e.target.value as AutomationAction)}
          >
            {ACTIONS.map((a) => (
              <option key={a.value} value={a.value}>
                {a.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="console-form__row">
        <div className="console-field">
          <label htmlFor="auto-new-param">Action param</label>
          <input
            id="auto-new-param"
            value={actionParam}
            placeholder={paramHint(action)}
            onChange={(e) => setActionParam(e.target.value)}
          />
        </div>
        <div className="console-field">
          <label htmlFor="auto-new-delay">Delay (minutes)</label>
          <input
            id="auto-new-delay"
            type="number"
            min="0"
            value={delay}
            onChange={(e) => setDelay(e.target.value)}
          />
        </div>
      </div>
      <button type="submit" className="console-btn" disabled={busy}>
        {busy ? "Creating…" : "Create rule"}
      </button>
    </form>
  );
}

function RulesPanel({
  rules,
  onCreate,
  onToggle,
  onDelete,
  busy,
  togglingId,
  deletingId,
}: {
  readonly rules: readonly AutomationRule[];
  readonly onCreate: (input: {
    name: string;
    trigger: AutomationTrigger;
    action: AutomationAction;
    actionParam: string | null;
    delayMinutes: number;
  }) => void;
  readonly onToggle: (rule: AutomationRule) => void;
  readonly onDelete: (rule: AutomationRule) => void;
  readonly busy: boolean;
  readonly togglingId: string | null;
  readonly deletingId: string | null;
}): ReactElement {
  return (
    <section className="console-panel" aria-label="Automation rules">
      <h2 className="console-panel__title">Rules</h2>
      <p className="recon-caption">
        Rules map a guest-lifecycle trigger to an action, optionally after a
        delay. Fire a trigger below to run every enabled matching rule.
      </p>
      {rules.length > 0 ? (
        <table className="console-table">
          <caption className="console-sr-only">Automation rules</caption>
          <thead>
            <tr>
              <th scope="col">Name</th>
              <th scope="col">Trigger</th>
              <th scope="col">Action</th>
              <th scope="col">Delay</th>
              <th scope="col">Enabled</th>
              <th scope="col">Action</th>
            </tr>
          </thead>
          <tbody>
            {rules.map((r) => (
              <tr key={r.id}>
                <td>{r.name}</td>
                <td>{triggerLabel(r.trigger)}</td>
                <td>{actionLabel(r.action)}</td>
                <td className="recon-num">
                  {r.delayMinutes === 0 ? "—" : `${r.delayMinutes} min`}
                </td>
                <td>
                  <button
                    type="button"
                    className={
                      r.enabled
                        ? "crm-optout crm-optout--no"
                        : "crm-optout crm-optout--yes"
                    }
                    disabled={togglingId === r.id}
                    onClick={() => onToggle(r)}
                  >
                    {togglingId === r.id
                      ? "…"
                      : r.enabled
                        ? "Enabled"
                        : "Disabled"}
                  </button>
                </td>
                <td>
                  <button
                    type="button"
                    className="console-btn console-btn--ghost"
                    disabled={deletingId === r.id}
                    onClick={() => onDelete(r)}
                  >
                    {deletingId === r.id ? "Deleting…" : "Delete"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="console-empty">
          No automation rules yet — create one with the form below.
        </p>
      )}

      <NewRuleForm onCreate={onCreate} busy={busy} />
    </section>
  );
}

// ===========================================================================
// Fire trigger (M8)
// ===========================================================================

function FirePanel({
  onFire,
  firing,
  result,
}: {
  readonly onFire: (trigger: AutomationTrigger) => void;
  readonly firing: boolean;
  readonly result: FireResult | null;
}): ReactElement {
  const [trigger, setTrigger] = useState<AutomationTrigger>("booking_confirmed");

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    onFire(trigger);
  }

  return (
    <section className="console-panel" aria-label="Fire trigger">
      <h2 className="console-panel__title">Fire trigger</h2>
      <p className="recon-caption">
        Simulate a lifecycle event: the automation engine plans and logs every
        enabled matching rule, then reports the counts.
      </p>
      <form className="console-form" onSubmit={handleSubmit} noValidate>
        <div className="console-form__row">
          <div className="console-field">
            <label htmlFor="auto-fire-trigger">Trigger</label>
            <select
              id="auto-fire-trigger"
              value={trigger}
              onChange={(e) => setTrigger(e.target.value as AutomationTrigger)}
            >
              {TRIGGERS.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <button type="submit" className="console-btn" disabled={firing}>
          {firing ? "Firing…" : "Fire trigger"}
        </button>
      </form>

      {result !== null ? (
        <div className="crm-detail" aria-live="polite">
          <h3 className="console-panel__title">Result</h3>
          <p className="crm-detail__field">
            <span className="crm-detail__label">Planned: </span>
            <span className="crm-detail__value">{result.planned}</span>
          </p>
          <p className="crm-detail__field">
            <span className="crm-detail__label">Executed: </span>
            <span className="crm-detail__value">{result.executed}</span>
          </p>
          <p className="crm-detail__field">
            <span className="crm-detail__label">Skipped: </span>
            <span className="crm-detail__value">{result.skipped}</span>
          </p>
        </div>
      ) : null}
    </section>
  );
}

// ===========================================================================
// Run log (M8)
// ===========================================================================

/** Format an ISO timestamp for display, tolerant of null. */
function formatTime(iso: string | null): string {
  if (iso === null) {
    return "—";
  }
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

/** The CSS class for a run-status badge. */
function statusClass(status: string): string {
  switch (status) {
    case "executed":
      return "crm-optout crm-optout--no";
    case "skipped":
      return "crm-optout crm-optout--yes";
    default:
      return "console-btn console-btn--ghost";
  }
}

function RunLogPanel({
  runs,
}: {
  readonly runs: readonly AutomationRun[];
}): ReactElement {
  return (
    <section className="console-panel" aria-label="Run log">
      <h2 className="console-panel__title">Run log</h2>
      <p className="recon-caption">
        The most recent automation runs, newest first.
      </p>
      {runs.length > 0 ? (
        <table className="console-table">
          <caption className="console-sr-only">Automation run log</caption>
          <thead>
            <tr>
              <th scope="col">Trigger</th>
              <th scope="col">Action</th>
              <th scope="col">Status</th>
              <th scope="col">Detail</th>
              <th scope="col">Time</th>
            </tr>
          </thead>
          <tbody>
            {runs.map((run) => (
              <tr key={run.id}>
                <td>{triggerLabel(run.trigger)}</td>
                <td>{actionLabel(run.action)}</td>
                <td>
                  <span className={statusClass(run.status)}>{run.status}</span>
                </td>
                <td>{run.detail}</td>
                <td>{formatTime(run.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="console-empty">
          No runs yet — fire a trigger to produce one.
        </p>
      )}
    </section>
  );
}

// ===========================================================================
// Top-level screen
// ===========================================================================

type LoadState =
  | { readonly kind: "loading" }
  | {
      readonly kind: "ready";
      readonly rules: readonly AutomationRule[];
      readonly runs: readonly AutomationRun[];
    }
  | { readonly kind: "error" };

export default function AutomationScreen(): ReactElement {
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [apiError, setApiError] = useState<UserMessageModel | null>(null);
  const [status, setStatus] = useState<UserMessageModel | null>(null);

  const [createBusy, setCreateBusy] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const [firing, setFiring] = useState(false);
  const [fireResult, setFireResult] = useState<FireResult | null>(null);

  const load = useCallback(async (): Promise<{
    rules: readonly AutomationRule[];
    runs: readonly AutomationRun[];
  }> => {
    const [rules, runs] = await Promise.all([
      apiListAutomationRules(),
      apiListAutomationRuns(),
    ]);
    return { rules, runs };
  }, []);

  const refresh = useCallback(async (): Promise<void> => {
    try {
      const { rules, runs } = await load();
      setState({ kind: "ready", rules, runs });
    } catch {
      setState({ kind: "error" });
    }
  }, [load]);

  useEffect(() => {
    let active = true;
    load()
      .then(({ rules, runs }) => {
        if (active) {
          setState({ kind: "ready", rules, runs });
        }
      })
      .catch(() => {
        if (active) {
          setState({ kind: "error" });
        }
      });
    return () => {
      active = false;
    };
  }, [load]);

  const handleCreate = useCallback(
    async (input: {
      name: string;
      trigger: AutomationTrigger;
      action: AutomationAction;
      actionParam: string | null;
      delayMinutes: number;
    }): Promise<void> => {
      setApiError(null);
      setStatus(null);
      setCreateBusy(true);
      try {
        const result = await apiCreateAutomationRule(input);
        const err = asError(result);
        if (err !== null) {
          setApiError(toUserMessage(err));
          return;
        }
        setStatus({
          what: "Automation rule created.",
          why: "The rule has been added and will run when its trigger fires.",
          next: "Fire the matching trigger to see its effect.",
        });
        await refresh();
      } catch {
        setApiError({
          what: "Could not create the rule.",
          why: "The request to the automation service failed.",
          next: "Check the API server is running and try again.",
        });
      } finally {
        setCreateBusy(false);
      }
    },
    [refresh],
  );

  const handleToggle = useCallback(
    async (rule: AutomationRule): Promise<void> => {
      setApiError(null);
      setStatus(null);
      setTogglingId(rule.id);
      try {
        const result = await apiSetAutomationRuleEnabled({
          ruleId: rule.id,
          enabled: !rule.enabled,
        });
        const err = asError(result);
        if (err !== null) {
          setApiError(toUserMessage(err));
          return;
        }
        await refresh();
      } catch {
        setApiError({
          what: "Could not update the rule.",
          why: "The request to the automation service failed.",
          next: "Check the API server is running and try again.",
        });
      } finally {
        setTogglingId(null);
      }
    },
    [refresh],
  );

  const handleDelete = useCallback(
    async (rule: AutomationRule): Promise<void> => {
      setApiError(null);
      setStatus(null);
      setDeletingId(rule.id);
      try {
        const result = await apiDeleteAutomationRule(rule.id);
        const err = asError(result);
        if (err !== null) {
          setApiError(toUserMessage(err));
          return;
        }
        setStatus({
          what: "Automation rule deleted.",
          why: `"${rule.name}" has been removed.`,
          next: "No action required.",
        });
        await refresh();
      } catch {
        setApiError({
          what: "Could not delete the rule.",
          why: "The request to the automation service failed.",
          next: "Check the API server is running and try again.",
        });
      } finally {
        setDeletingId(null);
      }
    },
    [refresh],
  );

  const handleFire = useCallback(
    async (trigger: AutomationTrigger): Promise<void> => {
      setApiError(null);
      setStatus(null);
      setFiring(true);
      try {
        const result = await apiFireTrigger(trigger);
        const err = asError(result);
        if (err !== null) {
          setFireResult(null);
          setApiError(toUserMessage(err));
          return;
        }
        setFireResult(result as FireResult);
        await refresh();
      } catch {
        setFireResult(null);
        setApiError({
          what: "Could not fire the trigger.",
          why: "The request to the automation service failed.",
          next: "Check the API server is running and try again.",
        });
      } finally {
        setFiring(false);
      }
    },
    [refresh],
  );

  return (
    <section aria-labelledby="automation-heading" data-console-screen="automation">
      <p className="console-page-eyebrow">Lifecycle · Automation</p>
      <h1 id="automation-heading">Automation</h1>
      <p className="console-lede">
        Trigger-based workflows across the guest lifecycle. Author rules that run
        an action — send a message, create a task, notify an operator, or apply a
        tag — when a lifecycle event fires, then fire a trigger to see them run.
      </p>

      {state.kind === "loading" ? (
        <p className="console-loading" role="status" aria-live="polite">
          Loading automation rules…
        </p>
      ) : null}

      {state.kind === "error" ? (
        <p className="console-empty" role="status">
          Automation data is unavailable right now. Try refreshing in a moment.
        </p>
      ) : null}

      {state.kind === "ready" ? (
        <>
          {apiError !== null ? (
            <UserMessage message={apiError} severity="error" />
          ) : null}
          {status !== null ? (
            <UserMessage message={status} severity="status" />
          ) : null}

          <RulesPanel
            rules={state.rules}
            onCreate={(input) => void handleCreate(input)}
            onToggle={(rule) => void handleToggle(rule)}
            onDelete={(rule) => void handleDelete(rule)}
            busy={createBusy}
            togglingId={togglingId}
            deletingId={deletingId}
          />

          <FirePanel
            onFire={(trigger) => void handleFire(trigger)}
            firing={firing}
            result={fireResult}
          />

          <RunLogPanel runs={state.runs} />
        </>
      ) : null}
    </section>
  );
}
